import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon, UserCircleIcon, GlobeIcon, Calendar03Icon, CreditCardIcon } from '@hugeicons/core-free-icons'

export interface AdminUserSummary {
  userId: string
  name: string
  email: string
  stripeCustomerId: string | null
  stripeCustomerUrl: string | null
  subscription: {
    subscriptionStripeId: string
    status: string
    product: string
    trial: boolean
    trialEnd: string | null
    stripeSubscriptionUrl: string
  } | null
  websites: string[]
  workspaces: Array<{
    workspaceId?: string
    workspaceName?: string
    accountOwnerId: string
    ownerName: string
    ownerEmail: string
    role?: string
    joinedAt: string | null
    isOwnWorkspace?: boolean
    isOwner?: boolean
  }>
}

interface UserHealthDrawerProps {
  user: AdminUserSummary | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onImpersonate: (userId: string, email: string) => void
  onCopyImpersonateLink?: (userId: string, email: string) => void
}

function subStatusBadge(sub: AdminUserSummary['subscription']) {
  if (!sub) return null
  if (sub.trial) return <Badge variant="secondary" className="text-[10px]">Trial</Badge>
  if (sub.status === 'ACTIVE') return <Badge variant="default" className="text-[10px]">Active</Badge>
  if (sub.status === 'CANCELED' || sub.status === 'CANCELLED') return <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40">Canceled</Badge>
  return <Badge variant="outline" className="text-[10px]">{sub.status}</Badge>
}

export default function UserHealthDrawer({ user, open, onOpenChange, onImpersonate, onCopyImpersonateLink }: UserHealthDrawerProps) {
  if (!user) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={UserCircleIcon} size={20} className="text-muted-foreground" />
            <SheetTitle className="text-base">{user.name || 'Unnamed user'}</SheetTitle>
          </div>
          <SheetDescription className="text-xs font-mono break-all">{user.email}</SheetDescription>
          <div className="text-[10px] font-mono text-muted-foreground/70 break-all">{user.userId}</div>
        </SheetHeader>

        <div className="mt-6 space-y-6">

          {/* Subscription */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <HugeiconsIcon icon={CreditCardIcon} size={14} className="text-muted-foreground" />
              <h3 className="text-xs font-medium text-muted-foreground">Subscription</h3>
            </div>
            {!user.subscription && !user.stripeCustomerId ? (
              <p className="text-xs text-muted-foreground">No subscription.</p>
            ) : (
              <div className="space-y-2 text-xs">
                {user.stripeCustomerId && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-24 shrink-0">Customer</span>
                    <span className="font-mono text-foreground">{user.stripeCustomerId}</span>
                    {user.stripeCustomerUrl && (
                      <a href={user.stripeCustomerUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 shrink-0">↗</a>
                    )}
                  </div>
                )}
                {user.subscription && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-24 shrink-0">Status</span>
                      {subStatusBadge(user.subscription)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-24 shrink-0">Product</span>
                      <span className="font-mono text-foreground">{user.subscription.product}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-24 shrink-0">Sub ID</span>
                      <span className="font-mono text-foreground truncate">{user.subscription.subscriptionStripeId}</span>
                      <a href={user.subscription.stripeSubscriptionUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 shrink-0">↗</a>
                    </div>
                    {user.subscription.trial && user.subscription.trialEnd && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground w-24 shrink-0">Trial ends</span>
                        <span className="text-foreground">{new Date(user.subscription.trialEnd).toLocaleDateString()}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

          {/* Sites */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <HugeiconsIcon icon={GlobeIcon} size={14} className="text-muted-foreground" />
              <h3 className="text-xs font-medium text-muted-foreground">Sites</h3>
              <Badge variant="secondary" className="text-[10px]">{user.websites.length}</Badge>
            </div>
            {user.websites.length === 0 ? (
              <p className="text-xs text-muted-foreground">No sites yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {user.websites.map((site, i) => (
                  <li key={i} className="text-sm font-mono text-foreground break-all">
                    {site}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Workspaces */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <HugeiconsIcon icon={Calendar03Icon} size={14} className="text-muted-foreground" />
              <h3 className="text-xs font-medium text-muted-foreground">Workspaces</h3>
              <Badge variant="secondary" className="text-[10px]">{user.workspaces.length}</Badge>
            </div>
            {user.workspaces.length === 0 ? (
              <p className="text-xs text-muted-foreground">No workspace memberships.</p>
            ) : (
              <ul className="space-y-3">
                {user.workspaces.map((w, i) => {
                  const isOwn = w.isOwnWorkspace ?? w.isOwner ?? false
                  return (
                    <li key={i} className="text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground font-medium">{w.workspaceName || w.ownerName}</span>
                        {isOwn ? (
                          <Badge variant="default" className="text-[9px] h-4 px-1.5">Own</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[9px] h-4 px-1.5">Member</Badge>
                        )}
                        {w.role && w.role !== 'owner' && (
                          <Badge variant="secondary" className="text-[9px] h-4 px-1.5">{w.role}</Badge>
                        )}
                      </div>
                      {!isOwn && (
                        <div className="text-muted-foreground mt-0.5">
                          Owner: <span className="font-medium text-foreground">{w.ownerName}</span>
                          {' · '}
                          <span className="font-mono">{w.ownerEmail}</span>
                        </div>
                      )}
                      {w.joinedAt && (
                        <div className="text-muted-foreground/70 mt-0.5">
                          Joined {new Date(w.joinedAt).toLocaleDateString()}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>

        <SheetFooter className="mt-8 flex-row gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {onCopyImpersonateLink && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onCopyImpersonateLink(user.userId, user.email)}
              title="Copy impersonation link to clipboard"
            >
              Copy link
            </Button>
          )}
          <Button
            className="flex-1"
            onClick={() => onImpersonate(user.userId, user.email)}
          >
            Impersonate
            <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="ml-1" />
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
